import type { WorkspaceMemoryGb, WorkspaceProfileId, WorkspaceRequestCreate } from "./types";

export const WORKSPACE_MEMORY_OPTIONS: WorkspaceMemoryGb[] = [16, 24, 32, 64, 128];
export const WORKSPACE_PROFILE_IDS: WorkspaceProfileId[] = [
  "interactive",
  "ios-build",
  "agent-workspace",
];
export const WORKSPACE_STORAGE_MIN = 128;
export const WORKSPACE_STORAGE_MAX = 4096;
export const WORKSPACE_DURATION_MIN = 1;
export const WORKSPACE_DURATION_MAX = 30;
export const WORKSPACE_NOTES_MAX = 2000;
export const WORKSPACE_PAGE_LIMIT = 10;

export function isWorkspaceMemory(value: number): value is WorkspaceMemoryGb {
  return (WORKSPACE_MEMORY_OPTIONS as number[]).includes(value);
}

export function isWorkspaceProfileId(value: string): value is WorkspaceProfileId {
  return (WORKSPACE_PROFILE_IDS as string[]).includes(value);
}

export function workspaceRequestFingerprint(
  projectId: string,
  payload: WorkspaceRequestCreate,
): string {
  return JSON.stringify({
    project_id: projectId,
    profile: payload.profile,
    memory_gb: payload.memory_gb,
    storage_gb: payload.storage_gb,
    duration_days: payload.duration_days,
    notes: payload.notes ?? "",
  });
}

export type WorkspaceRequestFieldErrors = Partial<
  Record<"wr-profile" | "wr-memory" | "wr-storage" | "wr-duration" | "wr-notes", string>
>;

export function validateWorkspaceRequestForm(
  input: {
    profile: string;
    memory: string;
    storage: string;
    duration: string;
    notes: string;
  },
  messages: {
    profileRequired: string;
    memoryRequired: string;
    storageRange: string;
    durationRange: string;
    notesMax: string;
  },
): { ok: true; payload: WorkspaceRequestCreate } | { ok: false; errors: WorkspaceRequestFieldErrors } {
  const errors: WorkspaceRequestFieldErrors = {};
  if (!isWorkspaceProfileId(input.profile)) errors["wr-profile"] = messages.profileRequired;

  const memory = Number(input.memory);
  if (!Number.isInteger(memory) || !isWorkspaceMemory(memory)) {
    errors["wr-memory"] = messages.memoryRequired;
  }

  const storage = Number(input.storage);
  if (
    !Number.isInteger(storage) ||
    storage < WORKSPACE_STORAGE_MIN ||
    storage > WORKSPACE_STORAGE_MAX
  ) {
    errors["wr-storage"] = messages.storageRange;
  }

  const duration = Number(input.duration);
  if (
    !Number.isInteger(duration) ||
    duration < WORKSPACE_DURATION_MIN ||
    duration > WORKSPACE_DURATION_MAX
  ) {
    errors["wr-duration"] = messages.durationRange;
  }

  if (input.notes.length > WORKSPACE_NOTES_MAX) errors["wr-notes"] = messages.notesMax;

  if (Object.keys(errors).length) return { ok: false, errors };

  return {
    ok: true,
    payload: {
      profile: input.profile as WorkspaceProfileId,
      memory_gb: memory as WorkspaceMemoryGb,
      storage_gb: storage,
      duration_days: duration,
      notes: input.notes.trim(),
    },
  };
}
